/**
 * Component 1: Project Charter
 * SMART Guide Template, Workshop metadata, Team management
 */

import React, { useState, useEffect } from 'react';
import { 
  User as UserIcon, 
  Calendar as CalendarIcon, 
  Image as PhotoIcon,
  Info as InformationCircleIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  X as XIcon,
  Plus as PlusIcon,
  Trash2 as TrashIcon,
  Users as UsersIcon,
  Target as TargetIcon
} from 'lucide-react';

import { useKataBoardV2Store } from '../../../stores/kataBoardV2Store';
import { textUtils, validationUtils, dateUtils } from '../../../utils/kataBoardV2Utils';
import type { KATABoardV2, Workshop, Outcome } from '../../../types/kataBoardV2';
import { useKataBoardCanEdit } from '../../../contexts/KataBoardAccessContext';
import { useAlert } from '../../../hooks/useAlert';
import { UserService, User } from '../../../services/user';
import SWOTAnalysisSection from './SWOTAnalysisSection';

interface Component01Props {
  kataBoard: KATABoardV2;
}

interface TeamMember {
  name: string;
  role: string;
  email?: string;
}

interface MenteeEntry {
  name: string;
  email?: string;
  role?: string;
}

interface ObjectiveStep {
  id: string;
  text: string;
  completed: boolean;
}

// Outcome Management Section Component
interface OutcomeManagementSectionProps {
  kataBoard: KATABoardV2;
  isEditing: boolean;
  onUpdate: () => void;
  onEdit: () => void;
  onDone: () => void;
}

interface NewOutcome {
  name: string;
  description: string;
  kpi_type: 'increase' | 'decrease';
  baseline_value: string;
  current_value: string;
  target_value: string;
  unit: string;
}

const OutcomeManagementSection: React.FC<OutcomeManagementSectionProps> = ({ kataBoard, isEditing, onUpdate, onEdit, onDone }) => {
  const canEdit = useKataBoardCanEdit();
  const { 
    createOutcome, 
    updateOutcome, 
    deleteOutcome,
    fetchKataBoardByWorkshop
  } = useKataBoardV2Store();
  
  const { confirmDelete, AlertComponent } = useAlert();
  
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [showAddOutcome, setShowAddOutcome] = useState(false);
  const [editingOutcome, setEditingOutcome] = useState<Outcome | null>(null);
  const [newOutcome, setNewOutcome] = useState<NewOutcome>({
    name: '',
    description: '',
    kpi_type: 'decrease',
    baseline_value: '',
    current_value: '',
    target_value: '',
    unit: ''
  });

  useEffect(() => {
    const currentOutcomes = kataBoard.outcomes?.filter(
      outcome => outcome.outcome_type === 'current'
    ) || [];
    setOutcomes(currentOutcomes);
  }, [kataBoard.outcomes]);

  const resetNewOutcome = () => {
    setNewOutcome({
      name: '',
      description: '',
      kpi_type: 'decrease',
      baseline_value: '',
      current_value: '',
      target_value: '',
      unit: ''
    });
  };

  const handleAddOutcome = async () => {
    try {
      await createOutcome({
        kata_board: kataBoard.id,
        outcome_type: 'current',
        name: newOutcome.name,
        description: newOutcome.description,
        kpi_type: newOutcome.kpi_type,
        baseline_value: newOutcome.baseline_value ? parseFloat(newOutcome.baseline_value) : null,
        current_value: newOutcome.current_value ? parseFloat(newOutcome.current_value) : null,
        target_value: newOutcome.target_value ? parseFloat(newOutcome.target_value) : null,
        unit: newOutcome.unit,
        order: outcomes.length
      });
      resetNewOutcome();
      setShowAddOutcome(false);
      await fetchKataBoardByWorkshop(kataBoard.workshop);
      onUpdate();
    } catch (error) {
      console.error('Failed to create outcome:', error);
    }
  };

  const handleEditOutcome = (outcome: Outcome) => {
    setEditingOutcome(outcome);
    setNewOutcome({
      name: outcome.name,
      description: outcome.description,
      kpi_type: outcome.kpi_type,
      baseline_value: outcome.baseline_value?.toString() || '',
      current_value: outcome.current_value?.toString() || '',
      target_value: outcome.target_value?.toString() || '',
      unit: outcome.unit
    });
    setShowAddOutcome(true);
  };

  const handleUpdateOutcome = async () => {
    if (!editingOutcome) return;
    
    try {
      await updateOutcome(editingOutcome.id, {
        name: newOutcome.name,
        description: newOutcome.description,
        kpi_type: newOutcome.kpi_type,
        baseline_value: newOutcome.baseline_value ? parseFloat(newOutcome.baseline_value) : null,
        current_value: newOutcome.current_value ? parseFloat(newOutcome.current_value) : null,
        target_value: newOutcome.target_value ? parseFloat(newOutcome.target_value) : null,
        unit: newOutcome.unit
      });
      resetNewOutcome();
      setShowAddOutcome(false);
      setEditingOutcome(null);
      await fetchKataBoardByWorkshop(kataBoard.workshop);
      onUpdate();
    } catch (error) {
      console.error('Failed to update outcome:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingOutcome(null);
    setShowAddOutcome(false);
    resetNewOutcome();
  };

  const handleDeleteOutcome = async (outcomeId: number, outcomeName: string) => {
    await confirmDelete(
      `"${outcomeName}" and all its factors`,
      async () => {
        try {
          await deleteOutcome(outcomeId);
          await fetchKataBoardByWorkshop(kataBoard.workshop);
          onUpdate();
        } catch (error) {
          console.error('Failed to delete outcome:', error);
        }
      }
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            Outcomes Management
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Define outcomes with baseline (initial), current, and target values
          </p>
        </div>
        {!isEditing ? (
          canEdit ? (
          <button
            onClick={onEdit}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <EditIcon className="h-4 w-4 mr-2" />
            Edit
          </button>
          ) : null
        ) : (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAddOutcome(true)}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Outcome
            </button>
            <button
              onClick={onDone}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <XIcon className="h-4 w-4 mr-2" />
              Done
            </button>
          </div>
        )}
      </div>

      {/* Outcomes List */}
      <div className="space-y-4">
        {outcomes.map((outcome) => (
          <div key={outcome.id} className="border border-gray-200 rounded-lg p-4">
            {/* Header with Edit/Delete buttons */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-medium text-gray-900">{outcome.name}</h4>
                {outcome.description && (
                  <p className="text-sm text-gray-600 mt-1">{outcome.description}</p>
                )}
              </div>
              {isEditing && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditOutcome(outcome)}
                    className="p-2 text-blue-500 hover:bg-blue-100 rounded"
                    title="Edit outcome"
                  >
                    <EditIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteOutcome(outcome.id, outcome.name)}
                    className="p-2 text-red-500 hover:bg-red-100 rounded"
                    title="Delete outcome"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            
            {/* Values Grid */}
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="bg-gray-50 p-3 rounded">
                <span className="font-medium text-gray-700">Baseline (Initial)</span>
                <div className="text-lg font-semibold text-gray-900 mt-1">
                  {outcome.baseline_value ? `${outcome.baseline_value} ${outcome.unit}` : 'Not set'}
                </div>
              </div>
              <div className="bg-red-50 p-3 rounded">
                <span className="font-medium text-red-700">Current</span>
                <div className="text-lg font-semibold text-red-600 mt-1">
                  {outcome.current_value ? `${outcome.current_value} ${outcome.unit}` : 'Not set'}
                </div>
              </div>
              <div className="bg-green-50 p-3 rounded">
                <span className="font-medium text-green-700">Target</span>
                <div className="text-lg font-semibold text-green-600 mt-1">
                  {outcome.target_value ? `${outcome.target_value} ${outcome.unit}` : 'Not set'}
                </div>
              </div>
            </div>
            
            {/* Metadata */}
            <div className="mt-2 text-xs text-gray-500">
              Type: {outcome.kpi_type === 'increase' ? 'Higher is better' : 'Lower is better'}
              {outcome.factors && outcome.factors.length > 0 && (
                <span className="ml-4">• {outcome.factors.length} factor(s) defined</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Outcome Form */}
      {showAddOutcome && isEditing && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-gray-900 mb-3">
            {editingOutcome ? 'Edit Outcome' : 'Add New Outcome'}
          </h4>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input
              type="text"
              placeholder="Outcome name"
              value={newOutcome.name}
              onChange={(e) => setNewOutcome({ ...newOutcome, name: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded"
            />
            <select
              value={newOutcome.kpi_type}
              onChange={(e) => setNewOutcome({ ...newOutcome, kpi_type: e.target.value as 'increase' | 'decrease' })}
              className="px-3 py-2 border border-gray-300 rounded"
            >
              <option value="decrease">Decrease Type (lower is better)</option>
              <option value="increase">Increase Type (higher is better)</option>
            </select>
          </div>
          <div className="grid grid-cols-4 gap-3 mb-3">
            <input
              type="text"
              placeholder="Baseline value"
              value={newOutcome.baseline_value}
              onChange={(e) => setNewOutcome({ ...newOutcome, baseline_value: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded"
            />
            <input
              type="text"
              placeholder="Current value"
              value={newOutcome.current_value}
              onChange={(e) => setNewOutcome({ ...newOutcome, current_value: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded"
            />
            <input
              type="text"
              placeholder="Target value"
              value={newOutcome.target_value}
              onChange={(e) => setNewOutcome({ ...newOutcome, target_value: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded"
            />
            <input
              type="text"
              placeholder="Unit (%, min, etc.)"
              value={newOutcome.unit}
              onChange={(e) => setNewOutcome({ ...newOutcome, unit: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded"
            />
          </div>
          <textarea
            placeholder="Description (optional)"
            value={newOutcome.description}
            onChange={(e) => setNewOutcome({ ...newOutcome, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded mb-3"
            rows={2}
          />
          <div className="flex justify-end space-x-2">
            <button
              onClick={handleCancelEdit}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={editingOutcome ? handleUpdateOutcome : handleAddOutcome}
              disabled={!newOutcome.name}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {editingOutcome ? 'Update Outcome' : 'Add Outcome'}
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {outcomes.length === 0 && !showAddOutcome && (
        <div className="text-center py-8 text-gray-500">
          <TargetIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm">No outcomes defined yet.</p>
          {isEditing && (
            <p className="text-sm mt-1">Click "Add Outcome" to get started.</p>
          )}
        </div>
      )}

      {AlertComponent}
    </div>
  );
};

// Utility functions for parsing mentors
const parseMentors = (mentorData: string | number): string[] => {
  if (!mentorData) return [];
  
  // If it's a number (user ID), convert to string and return as single item
  if (typeof mentorData === 'number') {
    return [mentorData.toString()];
  }
  
  // If it's a string, it could be either:
  // 1. A user ID as string (e.g., "39")
  // 2. A name or names separated by delimiters (e.g., "John Doe, Jane Smith")
  
  // Check if it's a numeric string (user ID)
  if (/^\d+$/.test(mentorData.trim())) {
    return [mentorData.trim()];
  }
  
  // Otherwise, split by various delimiters: newlines, forward slash, comma
  return mentorData
    .split(/[\n\/,]/)
    .map(mentor => mentor.trim())
    .filter(mentor => mentor.length > 0);
};

const formatMentors = (mentors: string[]): string => {
  return mentors.join(', ');
};

// Parse existing SMART objective into components
const parseSMARTObjective = (objective: string) => {
  if (!objective) return { verb: '', measurable: '', noun: '', current: '', target: '', time: '' };
  
  // Try to parse SMART objective pattern: "[Verb] the [Measurable] [Noun] from [Current] to [Target] by [Time]"
  const smartPattern = /^(\w+)\s+the\s+(.+?)\s+(.+?)\s+from\s+(.+?)\s+to\s+(.+?)\s+by\s+(.+)$/i;
  const match = objective.match(smartPattern);
  
  if (match) {
    let timeValue = match[6]?.trim() || '';
    
    // Try to convert date string back to YYYY-MM-DD format for date input
    if (timeValue) {
      try {
        const date = new Date(timeValue);
        if (!isNaN(date.getTime())) {
          timeValue = date.toISOString().split('T')[0]; // Convert to YYYY-MM-DD
        }
      } catch {
        // Keep original format if conversion fails
      }
    }
    
    return {
      verb: match[1]?.trim() || '',
      measurable: match[2]?.trim() || '',
      noun: match[3]?.trim() || '',
      current: match[4]?.trim() || '',
      target: match[5]?.trim() || '',
      time: timeValue
    };
  }
  
  // If pattern doesn't match, try to extract some common keywords
  const words = objective.toLowerCase().split(' ');
  const verb = words.find(word => ['increase', 'decrease', 'improve', 'reduce', 'enhance', 'optimize'].includes(word)) || '';
  
  return {
    verb: verb.charAt(0).toUpperCase() + verb.slice(1),
    measurable: '',
    noun: '',
    current: '',
    target: '',
    time: ''
  };
};


const Component01_GenericInfo: React.FC<Component01Props> = ({ kataBoard }) => {
  const {
    updateKataBoard,
    setEditing,
    setUnsavedChanges,
    createFileAttachment,
    fetchKataBoardByWorkshop
  } = useKataBoardV2Store();

  const [isEditing, setIsEditingLocal] = useState(false);
  const canEdit = useKataBoardCanEdit();
  const [formData, setFormData] = useState({
    smart_objective: kataBoard.smart_objective || '',
    mentor: kataBoard.workshop_info?.mentor || '',
    startup_date: kataBoard.workshop_info?.startup_date || '',
    deadline: kataBoard.workshop_info?.deadline || ''
  });
  
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [mentees, setMentees] = useState<MenteeEntry[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [mainObjective, setMainObjective] = useState('');
  const [objectiveSteps, setObjectiveSteps] = useState<ObjectiveStep[]>([]);
  
  // SMART Builder state
  const [smartBuilder, setSmartBuilder] = useState({
    verb: '',
    measurable: '',
    noun: '',
    current: '',
    target: '',
    time: ''
  });

  // Helper function to get user name by ID or return the name if it's already a name
  const getUserName = (userIdOrName: string | number): string => {
    if (!userIdOrName) return '';
    
    const idOrName = userIdOrName.toString();
    
    // If it's a numeric string or number, try to find the user by ID
    if (/^\d+$/.test(idOrName)) {
      if (!Array.isArray(users) || users.length === 0) {
        // If users not loaded yet, return ID with a loading indicator
        return usersLoaded ? idOrName : `Loading... (${idOrName})`;
      }
      const user = users.find(u => u.id === Number(idOrName));
      if (user) {
        const fullName = `${user.first_name} ${user.last_name}`.trim();
        return fullName || user.username || idOrName;
      }
      return idOrName;
    }
    
    // If it's not numeric, assume it's already a name
    return idOrName;
  };

  // Helper function to get user full name with email by ID or return the name if it's already a name
  const getUserFullInfo = (userIdOrName: string | number): { name: string; email: string } => {
    if (!userIdOrName) return { name: '', email: '' };
    
    const idOrName = userIdOrName.toString();
    
    // If it's a numeric string or number, try to find the user by ID
    if (/^\d+$/.test(idOrName)) {
      if (!Array.isArray(users) || users.length === 0) {
        // If users not loaded yet, return ID with loading indicator
        const loadingName = usersLoaded ? idOrName : `Loading... (${idOrName})`;
        return { name: loadingName, email: '' };
      }
      const user = users.find(u => u.id === Number(idOrName));
      if (user) {
        const fullName = `${user.first_name} ${user.last_name}`.trim();
        return { 
          name: fullName || user.username || idOrName,
          email: user.email || '' 
        };
      }
      return { name: idOrName, email: '' };
    }
    
    // If it's not numeric, assume it's already a name
    return { name: idOrName, email: '' };
  };

  useEffect(() => {
    setEditing(isEditing);
  }, [isEditing, setEditing]);

  // Load users for name lookup
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const fetchedUsers = await UserService.getAllUsers();
        setUsers(fetchedUsers);
        setUsersLoaded(true);
      } catch (error) {
        console.error('Failed to load users:', error);
        setUsersLoaded(true); // Set to true even on error to avoid infinite loading
      }
    };
    loadUsers();
  }, []);

  // Initialize data on component mount
  useEffect(() => {
    // Initialize team members from team_description if it exists
    if (kataBoard.team_description) {
      try {
        const parsedTeam = JSON.parse(kataBoard.team_description);
        if (Array.isArray(parsedTeam)) {
          setTeamMembers(parsedTeam);
        }
      } catch {
        // If not JSON, create a single team member from description
        setTeamMembers([{ name: kataBoard.team_description, role: 'Team Member' }]);
      }
    }

    // Initialize objective data from database
    setMainObjective(kataBoard.workshop_objective_title || '');
    setObjectiveSteps(kataBoard.workshop_objective_steps || []);
    
    // Initialize SMART builder with parsed values from existing objective
    const parsedSmart = parseSMARTObjective(kataBoard.smart_objective || '');
    setSmartBuilder(parsedSmart);
  }, [kataBoard.team_description, kataBoard.workshop_objective_title, kataBoard.workshop_objective_steps, kataBoard.smart_objective]);

  // Separate useEffect to handle mentees when users are loaded
  useEffect(() => {
    // Initialize mentees from workshop mentees field
    // Handle both cases: user IDs (numbers) and names (strings)
    const workshopMentees = (kataBoard.workshop_info as Workshop)?.mentees || [];
    const initialMentees = Array.isArray(workshopMentees) 
      ? workshopMentees.flatMap(mentee => {
          // Handle both string names/IDs and number user IDs
          if (typeof mentee === 'number') {
            // If it's a number (user ID), get the user name
            const userInfo = getUserFullInfo(mentee);
            return [{
              name: userInfo.name,
              email: userInfo.email,
              role: 'Mentee'
            }];
          } else if (typeof mentee === 'string') {
            // If it's a string, it could be:
            // 1. A user ID as string (e.g., "39")
            // 2. A name or names separated by delimiters (e.g., "John Doe, Jane Smith")
            return parseMentors(mentee).map(menteeIdOrName => {
              const userInfo = getUserFullInfo(menteeIdOrName);
              return {
                name: userInfo.name,
                email: userInfo.email,
                role: 'Mentee'
              };
            });
          }
          return [];
        })
      : [];
    setMentees(initialMentees);
  }, [(kataBoard.workshop_info as Workshop)?.mentees, users, usersLoaded]);

  const handleEdit = () => {
    if (!canEdit) return;
    setIsEditingLocal(true);
  };

  const handleCancel = () => {
    setFormData({
      smart_objective: kataBoard.smart_objective || '',
      mentor: kataBoard.workshop_info?.mentor || '',
      startup_date: kataBoard.workshop_info?.startup_date || '',
      deadline: kataBoard.workshop_info?.deadline || ''
    });
    setSelectedFile(null);
    setPreviewUrl(null);
    setValidationErrors({});
    setIsEditingLocal(false);
    setUnsavedChanges(false);
    
    // Reset team members
    if (kataBoard.team_description) {
      try {
        const parsedTeam = JSON.parse(kataBoard.team_description);
        if (Array.isArray(parsedTeam)) {
          setTeamMembers(parsedTeam);
        }
      } catch {
        setTeamMembers([{ name: kataBoard.team_description, role: 'Team Member' }]);
      }
    }
    
    // Reset mentees
    const workshopMentees = (kataBoard.workshop_info as Workshop)?.mentees || [];
    const initialMentees = Array.isArray(workshopMentees) 
      ? workshopMentees.flatMap(name => 
          // Parse each mentee name in case it contains multiple names separated by delimiters
          parseMentors(name).map(mentee => ({
            name: mentee,
            email: '',
            role: 'Mentee'
          }))
        )
      : [];
    setMentees(initialMentees);

    // Reset objective data
    setMainObjective(kataBoard.workshop_objective_title || '');
    setObjectiveSteps(kataBoard.workshop_objective_steps || []);
    
    // Reset SMART builder
    const parsedSmart = parseSMARTObjective(kataBoard.smart_objective || '');
    setSmartBuilder(parsedSmart);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate SMART objective
    if (!formData.smart_objective.trim()) {
      errors.smart_objective = 'SMART objective is required';
    }

    // Validate dates
    if (formData.startup_date && formData.deadline) {
      const startDate = new Date(formData.startup_date);
      const endDate = new Date(formData.deadline);
      if (startDate > endDate) {
        errors.deadline = 'Deadline must be after start date';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      // Update KATA board (including workshop image if selected)
      await updateKataBoard(kataBoard.id, {
        smart_objective: formData.smart_objective,
        team_description: JSON.stringify(teamMembers),
        workshop_objective_title: mainObjective,
        workshop_objective_steps: objectiveSteps,
        workshop_image: selectedFile // Include the image file
      });
      
      // Refresh the KATA board data to get updated information
      await fetchKataBoardByWorkshop(kataBoard.workshop);
      
      setIsEditingLocal(false);
      setUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setUnsavedChanges(true);
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setUnsavedChanges(true);
    }
  };

  // Team member management
  const addTeamMember = () => {
    setTeamMembers(prev => [...prev, { name: '', role: '', email: '' }]);
    setUnsavedChanges(true);
  };

  const updateTeamMember = (index: number, field: keyof TeamMember, value: string) => {
    setTeamMembers(prev => prev.map((member, i) => 
      i === index ? { ...member, [field]: value } : member
    ));
    setUnsavedChanges(true);
  };

  const removeTeamMember = (index: number) => {
    setTeamMembers(prev => prev.filter((_, i) => i !== index));
    setUnsavedChanges(true);
  };

  // Mentee management
  const addMentee = () => {
    setMentees(prev => [...prev, { name: '', email: '', role: 'Mentee' }]);
    setUnsavedChanges(true);
  };

  const updateMentee = (index: number, field: keyof MenteeEntry, value: string) => {
    setMentees(prev => prev.map((mentee, i) => 
      i === index ? { ...mentee, [field]: value } : mentee
    ));
    setUnsavedChanges(true);
  };

  const removeMentee = (index: number) => {
    setMentees(prev => prev.filter((_, i) => i !== index));
    setUnsavedChanges(true);
  };

  // Objective step management
  const addObjectiveStep = () => {
    const newStep: ObjectiveStep = {
      id: Date.now().toString(),
      text: '',
      completed: false
    };
    setObjectiveSteps(prev => [...prev, newStep]);
    setUnsavedChanges(true);
  };

  const updateObjectiveStep = (id: string, field: keyof ObjectiveStep, value: string | boolean) => {
    setObjectiveSteps(prev => prev.map(step => 
      step.id === id ? { ...step, [field]: value } : step
    ));
    setUnsavedChanges(true);
  };

  const removeObjectiveStep = (id: string) => {
    setObjectiveSteps(prev => prev.filter(step => step.id !== id));
    setUnsavedChanges(true);
  };

  return (
    <div className="space-y-8">
      {/* Project Charter Overview Image */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex justify-center">
          <img 
            src="/images/project_charter.png" 
            alt="Project Charter Overview - KATA Methodology" 
            className="max-w-full h-auto rounded-lg shadow-sm"
            style={{ maxHeight: '400px' }}
          />
        </div>
        <p className="text-center text-sm text-gray-600 mt-3">
          Project Charter Overview - KATA Methodology Framework
        </p>
      </div>

      {/* SMART Guide Template */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-lg font-medium text-blue-900 mb-3">
              SMART Guide Template
            </h3>
            <p className="text-sm text-blue-800 mb-4">
              Template for an effective SMART Objective Statement:
            </p>
            
            <div className="bg-white rounded-md p-4 border border-blue-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Verb
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Measurable
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Noun
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        From (Current)
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        To (Target)
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        By (Time)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-3 py-2 text-sm text-gray-900">Decrease</td>
                      <td className="px-3 py-2 text-sm text-gray-900"><strong>The</strong> Changeover time</td>
                      <td className="px-3 py-2 text-sm text-gray-900">On T-Cell</td>
                      <td className="px-3 py-2 text-sm text-gray-900"><strong>From</strong> 15 minutes</td>
                      <td className="px-3 py-2 text-sm text-gray-900"><strong>To</strong> 5 minutes</td>
                      <td className="px-3 py-2 text-sm text-gray-900"><strong>By</strong> 05-Apr-25</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4 p-3 bg-gray-50 rounded-md">
                <p className="text-sm font-medium text-gray-900">Example Statement:</p>
                <p className="text-sm text-gray-700 italic">
                  "Decrease the changeover time on T-Cell from 15 minutes to 5 minutes by April 5, 2025."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Workshop Information Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Workshop Information</h3>
          {!isEditing ? (
            canEdit ? (
            <button
              onClick={handleEdit}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <EditIcon className="h-4 w-4 mr-2" />
              Edit
            </button>
            ) : null
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={handleCancel}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <XIcon className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <SaveIcon className="h-4 w-4 mr-2" />
                Save
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* SMART Objective */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMART Objective Statement *
              </label>
              {isEditing ? (
                <div className="space-y-4">
                  {/* Interactive SMART Builder */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-3">SMART Objective Builder</h4>
                    <div className="flex flex-wrap items-end gap-2 text-sm">
                      {/* Verb */}
                      <div className="flex-1 min-w-[100px]">
                        <label className="block text-xs font-medium text-blue-700 mb-1">Verb</label>
                        <input
                          type="text"
                          placeholder="Decrease, Increase..."
                          className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          value={smartBuilder.verb}
                          onChange={(e) => setSmartBuilder(prev => ({ ...prev, verb: e.target.value }))}
                        />
                      </div>

                      {/* "the" connector */}
                      <div className="px-2 py-1 text-blue-800 font-medium">the</div>
                      
                      {/* Measurable */}
                      <div className="flex-1 min-w-[80px]">
                        <label className="block text-xs font-medium text-blue-700 mb-1">Measurable</label>
                        <input
                          type="text"
                          placeholder="changeover time ..."
                          className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          value={smartBuilder.measurable}
                          onChange={(e) => setSmartBuilder(prev => ({ ...prev, measurable: e.target.value }))}
                        />
                      </div>
                      
                      {/* Noun */}
                      <div className="flex-1 min-w-[150px]">
                        <label className="block text-xs font-medium text-blue-700 mb-1">Noun</label>
                        <input
                          type="text"
                          placeholder="on T-Cell..."
                          className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          value={smartBuilder.noun}
                          onChange={(e) => setSmartBuilder(prev => ({ ...prev, noun: e.target.value }))}
                        />
                      </div>
                      
                      {/* "from" connector */}
                      <div className="px-2 py-1 text-blue-800 font-medium">from</div>
                      
                      {/* Current */}
                      <div className="flex-1 min-w-[100px]">
                        <label className="block text-xs font-medium text-blue-700 mb-1">Current</label>
                        <input
                          type="text"
                          placeholder="15 minutes..."
                          className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          value={smartBuilder.current}
                          onChange={(e) => setSmartBuilder(prev => ({ ...prev, current: e.target.value }))}
                        />
                      </div>
                      
                      {/* "to" connector */}
                      <div className="px-2 py-1 text-blue-800 font-medium">to</div>
                      
                      {/* Target */}
                      <div className="flex-1 min-w-[100px]">
                        <label className="block text-xs font-medium text-blue-700 mb-1">Target</label>
                        <input
                          type="text"
                          placeholder="5 minutes..."
                          className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          value={smartBuilder.target}
                          onChange={(e) => setSmartBuilder(prev => ({ ...prev, target: e.target.value }))}
                        />
                      </div>
                      
                      {/* "by" connector */}
                      <div className="px-2 py-1 text-blue-800 font-medium">by</div>
                      
                      {/* Time */}
                      <div className="flex-1 min-w-[120px]">
                        <label className="block text-xs font-medium text-blue-700 mb-1">Time</label>
                        <input
                          type="date"
                          className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          value={smartBuilder.time}
                          onChange={(e) => setSmartBuilder(prev => ({ ...prev, time: e.target.value }))}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        // Format date if provided
                        let formattedTime = smartBuilder.time;
                        if (smartBuilder.time) {
                          try {
                            const date = new Date(smartBuilder.time);
                            formattedTime = date.toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            });
                          } catch {
                            formattedTime = smartBuilder.time;
                          }
                        }
                        
                        const smartStatement = `${smartBuilder.verb} the ${smartBuilder.measurable} ${smartBuilder.noun} from ${smartBuilder.current} to ${smartBuilder.target} by ${formattedTime}`.replace(/\s+/g, ' ').trim();
                        handleInputChange('smart_objective', smartStatement);
                      }}
                      className="mt-3 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Generate SMART Statement
                    </button>
                  </div>

                  {/* Manual Text Area */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Or enter manually:
                    </label>
                    <textarea
                      value={formData.smart_objective}
                      onChange={(e) => handleInputChange('smart_objective', e.target.value)}
                      rows={3}
                      className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        validationErrors.smart_objective 
                          ? 'border-red-300 focus:ring-red-500' 
                          : 'border-gray-300'
                      }`}
                      placeholder="Enter a SMART objective following the template above..."
                    />
                    {validationErrors.smart_objective && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.smart_objective}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-gray-50 rounded-md border">
                  {kataBoard.smart_objective ? (
                    <p className="text-sm text-gray-900">{kataBoard.smart_objective}</p>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No SMART objective defined</p>
                  )}
                </div>
              )}
            </div>

            {/* Mentor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <UserIcon className="h-4 w-4 inline mr-1" />
                Mentor
              </label>
              {isEditing ? (
                <div>
                  <textarea
                    value={formData.mentor}
                    onChange={(e) => handleInputChange('mentor', e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter mentor name(s). Use commas, forward slashes, or new lines to separate multiple mentors"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    💡 For multiple mentors, use: <code>John Doe, Jane Smith</code> or <code>John Doe / Jane Smith</code>
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-gray-50 rounded-md border">
                  {kataBoard.workshop_info?.mentor ? (
                    <div>
                      {parseMentors(kataBoard.workshop_info.mentor).map((mentorId, index) => {
                        const mentorName = getUserName(mentorId);
                        return (
                          <div key={index} className="flex items-center mb-2 last:mb-0">
                            <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium mr-3">
                              {textUtils.getInitials(mentorName)}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {mentorName}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No mentor assigned</p>
                  )}
                </div>
              )}
            </div>

            {/* Timeline */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  Start Date
                </label>
                {isEditing ? (
                  <input
                    type="date"
                    value={formData.startup_date}
                    onChange={(e) => handleInputChange('startup_date', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <div className="p-3 bg-gray-50 rounded-md border">
                    <p className="text-sm text-gray-900">
                      {kataBoard.workshop_info?.startup_date ? 
                        dateUtils.formatDate(kataBoard.workshop_info.startup_date) : 
                        'Not set'
                      }
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  Deadline
                </label>
                {isEditing ? (
                  <div>
                    <input
                      type="date"
                      value={formData.deadline}
                      onChange={(e) => handleInputChange('deadline', e.target.value)}
                      className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        validationErrors.deadline 
                          ? 'border-red-300 focus:ring-red-500' 
                          : 'border-gray-300'
                      }`}
                    />
                    {validationErrors.deadline && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.deadline}</p>
                    )}
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-md border">
                    <p className="text-sm text-gray-900">
                      {kataBoard.workshop_info?.deadline ? 
                        dateUtils.formatDate(kataBoard.workshop_info.deadline) : 
                        'Not set'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Workshop Metadata */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Workshop Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Workshop Number:</span>
                  <p className="font-medium">{kataBoard.workshop_info?.workshop_number}</p>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <p className="font-medium">{kataBoard.workshop_info?.status}</p>
                </div>
                <div>
                  <span className="text-gray-500">Category:</span>
                  <p className="font-medium">{kataBoard.workshop_info?.cc_category}</p>
                </div>
                <div>
                  <span className="text-gray-500">SQDCM:</span>
                  <p className="font-medium">{kataBoard.workshop_info?.sqdcm_category}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Workshop Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <PhotoIcon className="h-4 w-4 inline mr-1" />
                Workshop Image
              </label>
              
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>

                  {previewUrl && (
                    <div className="relative">
                      <img
                        src={previewUrl}
                        alt="Workshop preview"
                        className="w-full h-48 object-cover rounded-md border"
                      />
                      <button
                        onClick={() => {
                          setSelectedFile(null);
                          setPreviewUrl(null);
                          if (previewUrl) URL.revokeObjectURL(previewUrl);
                        }}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  {kataBoard.workshop_image ? (
                    <img
                      src={kataBoard.workshop_image}
                      alt="Workshop"
                      className="w-full h-48 object-cover rounded-md"
                    />
                  ) : (
                    <div className="text-center">
                      <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-500">No workshop image uploaded</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mentees Management */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">
                  <UsersIcon className="h-4 w-4 inline mr-1" />
                  Mentees
                </h4>
                {isEditing && (
                  <button
                    onClick={addMentee}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200"
                  >
                    <PlusIcon className="h-3 w-3 mr-1" />
                    Add Mentee
                  </button>
                )}
              </div>

              {mentees.length > 0 ? (
                <div className="space-y-3">
                  {mentees.map((mentee, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-md border">
                      {isEditing ? (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={mentee.name}
                              onChange={(e) => updateMentee(index, 'name', e.target.value)}
                              placeholder="Mentee Name"
                              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                            <button
                              onClick={() => removeMentee(index)}
                              className="p-1 text-red-500 hover:bg-red-100 rounded"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="email"
                              value={mentee.email || ''}
                              onChange={(e) => updateMentee(index, 'email', e.target.value)}
                              placeholder="Email (optional)"
                              className="border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                            <input
                              type="text"
                              value={mentee.role || ''}
                              onChange={(e) => updateMentee(index, 'role', e.target.value)}
                              placeholder="Role"
                              className="border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <div className="h-6 w-6 rounded-full bg-green-500 flex items-center justify-center text-white text-xs font-medium mr-2">
                            {textUtils.getInitials(mentee.name)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{mentee.name}</p>
                            <p className="text-xs text-gray-500">{mentee.role || 'Mentee'}</p>
                            {mentee.email && (
                              <p className="text-xs text-gray-400">{mentee.email}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No mentees assigned</p>
              )}
            </div>

            {/* Team Members */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">Team Members</h4>
                {isEditing && (
                  <button
                    onClick={addTeamMember}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200"
                  >
                    <PlusIcon className="h-3 w-3 mr-1" />
                    Add Member
                  </button>
                )}
              </div>

              {teamMembers.length > 0 ? (
                <div className="space-y-3">
                  {teamMembers.map((member, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-md border">
                      {isEditing ? (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={member.name}
                              onChange={(e) => updateTeamMember(index, 'name', e.target.value)}
                              placeholder="Name"
                              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                            <button
                              onClick={() => removeTeamMember(index)}
                              className="p-1 text-red-500 hover:bg-red-100 rounded"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={member.role}
                              onChange={(e) => updateTeamMember(index, 'role', e.target.value)}
                              placeholder="Role"
                              className="border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                            <input
                              type="email"
                              value={member.email || ''}
                              onChange={(e) => updateTeamMember(index, 'email', e.target.value)}
                              placeholder="Email (optional)"
                              className="border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm font-medium text-gray-900">{member.name}</p>
                          <p className="text-xs text-gray-500">{member.role}</p>
                          {member.email && (
                            <p className="text-xs text-gray-400">{member.email}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No team members added</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Workshop Objective Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Workshop Objective</h3>
          {!isEditing ? (
            canEdit ? (
            <button
              onClick={handleEdit}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <EditIcon className="h-4 w-4 mr-2" />
              Edit
            </button>
            ) : null
          ) : null}
        </div>

        {isEditing ? (
          <div className="space-y-4">
            {/* Main Objective Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Main Objective Title
              </label>
              <input
                type="text"
                value={mainObjective}
                onChange={(e) => {
                  setMainObjective(e.target.value);
                  setUnsavedChanges(true);
                }}
                placeholder="e.g., Increase APS & Q5 Behaviors"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Objective Steps */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Action Steps
                </label>
                <button
                  onClick={addObjectiveStep}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200"
                >
                  <PlusIcon className="h-3 w-3 mr-1" />
                  Add Step
                </button>
              </div>

              <div className="space-y-2">
                {objectiveSteps.map((step) => (
                  <div key={step.id} className="flex items-center space-x-3 p-2 bg-gray-50 rounded-md">
                    <input
                      type="checkbox"
                      checked={step.completed}
                      onChange={(e) => updateObjectiveStep(step.id, 'completed', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      value={step.text}
                      onChange={(e) => updateObjectiveStep(step.id, 'text', e.target.value)}
                      placeholder="Enter step description"
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => removeObjectiveStep(step.id)}
                      className="p-1 text-red-500 hover:bg-red-100 rounded"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div>
            {/* Display Main Objective */}
            {mainObjective ? (
              <div className="mb-4">
                <h4 className="text-xl font-semibold text-blue-900 mb-3">
                  {mainObjective} :
                </h4>
              </div>
            ) : (
              <p className="text-gray-500 italic mb-4">No main objective set</p>
            )}

            {/* Display Steps */}
            {objectiveSteps.length > 0 ? (
              <div className="space-y-2">
                {objectiveSteps.map((step) => (
                  <div key={step.id} className="flex items-center space-x-3">
                    <div className={`text-lg ${step.completed ? 'text-green-600' : 'text-gray-400'}`}>
                      {step.completed ? '✓' : '○'}
                    </div>
                    <span className={`text-sm ${step.completed ? 'text-gray-900' : 'text-gray-600'}`}>
                      {step.text}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic">No action steps defined</p>
            )}
          </div>
        )}
      </div>

      {/* Outcomes Management */}
      <OutcomeManagementSection 
        kataBoard={kataBoard}
        isEditing={isEditing}
        onUpdate={handleSave}
        onEdit={handleEdit}
        onDone={handleCancel}
      />

      {/* SWOT Analysis Section */}
      <SWOTAnalysisSection 
        kataBoard={kataBoard}
        onUpdate={handleSave}
      />
    </div>
  );
};

export default Component01_GenericInfo;
